/**
 * Build step 8 — meetings, documents, opportunities, email reading (ADR-001 §11.10, as amended by ADR-002, ADR-003
 * and ADR-004; operator decisions 3, 4, 5, 6, 13, 15). See meetings.ts, mail.ts, documents.ts and whatsapp.ts for
 * the rules, and docs/architecture/step8-contract-decisions.md for where the sources differ.
 *
 * Visibility follows the client: managers all; an ops member the clients they are the current SPOC of; requesters
 * and HR none. Out-of-scope ids are 404, never 403. Writes: a manager, or the client's current SPOC (the handler
 * checks the SPOC; others 404 when they cannot see the row, 403 when they can). Every write is audited (real
 * actor + acting-as); viewing-as is read-only (403). Revenue (`value_lakh`) is absent for members.
 *
 * Additions beyond the §11.10 table, each for something the sources require: the Google connection routes of
 * ADR-003 §9 (email reading cannot start without them); `GET /meetings/{id}/media-url` (the prototype's Play),
 * `POST /meetings/document/read` (a minutes document read to text, nothing kept; the Meetings redesign removed
 * `POST /meetings/sign-media` and `POST /meetings/{id}/retranscribe`), `GET /email-threads/{id}` and `POST /email-threads/{id}/task`
 * (the Email drawer and "Make task"), `GET /documents/{id}`, `POST /documents/sign-upload` and
 * `GET /documents/{id}/versions/{vid}/file` (upload and Open), `GET /opportunities/{id}` and
 * `PATCH /opportunities/{id}` (setting the value), and `GET/PATCH /settings/privacy` (the consent and tone-label
 * switches §11.10 names, and audio retention — operator decision 15).
 */
import { z } from 'zod';
import { IdempotencyHeaders, IfMatchHeaders, OptionalIfMatchHeaders } from './common.js';
import { AddDocumentVersionBody, CreateDocumentBody, CreateOpportunityBody, Document, DocumentPage, DocumentVersionParams, ListDocumentsQuery, ListOpportunitiesQuery, MoveDocumentBody, MoveOpportunityBody, Opportunity, OpportunityManager, OpportunityMember, OpportunityPage, OpportunityPageManager, OpportunityPageMember, SignDocumentUploadBody, UpdateDocumentBody, UpdateOpportunityBody, } from './documents.js';
import { ConfirmProposedBody, ConfirmProposedResponse, DisconnectGoogleResponse, EmailThread, EmailThreadManager, EmailThreadMember, EmailThreadPage, EmailThreadPageManager, EmailThreadPageMember, MadeFromEmailManager, MadeFromEmailMember, UpdateEmailThreadResponseManager, UpdateEmailThreadResponseMember, GoogleConnectCallbackQuery, GoogleConnectQuery, GoogleCoverage, GoogleScopeParams, ListEmailThreadsQuery, ListProposedItemsQuery, MadeFromEmail, MakeThreadTaskBody, MyGoogleConnection, ProposedItemPage, RejectProposedBody, RejectProposedResponse, ResyncResponse, UpdateEmailThreadBody, UpdateEmailThreadResponse, GoogleUserParams, } from './mail.js';
import { ConfirmMeetingBody, ConfirmMeetingResponse, CreateMeetingBody, ReadMinutesDocumentBody, ReadMinutesDocumentResponse, ListMeetingsQuery, MediaUrlResponse, Meeting, MeetingPage, MINUTES_FILE_MAX_BYTES, UpdateMeetingBody, UploadSlot, } from './meetings.js';
import { MarkWhatsAppSentBody, MarkWhatsAppSentResponse, PatchPrivacySettingsBody, PrivacySettings, WhatsAppDraft, WhatsAppDraftQuery, } from './whatsapp.js';
import { API_BASE, defineRoute } from './route.js';
const IdOnly = z.object({ id: z.uuid() });
/** Founder, associate director, ops member — everyone who works with clients. Requesters and HR: none. */
const CLIENT_WORK = ['clients.read.all', 'clients.read.own'];
const MANAGERS = ['team.read'];
const MANAGER_SETTINGS = ['settings.write'];
const LIST_SCOPE = 'Managers → all clients; an ops member → the clients they are SPOC of. Others: 403.';
const SCOPE = 'Managers; or the client’s current SPOC. Others: 404.';
const WRITE_SCOPE = 'Managers, or the client’s current SPOC. Others: 404 (403 when they can see it). Not while viewing as someone (403).';
/** An unmatched thread has no client yet: its SPOC side is the candidates the caller is SPOC of. */
const THREAD_WRITE_SCOPE = 'Managers, or the current SPOC of the thread’s client — of one of its candidate clients while unmatched, and then ' +
    'only into a client of their own (403 otherwise). Others: 404 (403 when they can see it). Not while viewing as someone (403).';
const DECIDE_SCOPE = 'The suggested assignee, the client’s current SPOC, or a manager. Others: 404 (403 when they can see it). Not while viewing as someone (403).';
const SELF = 'The signed-in person, about their own Google account. Founder, associate director and ops members.';
const ETAG = { ETag: 'W/"<version>" of the returned resource' };
const CONFIG_ETAG = { ETag: 'W/"<config_version>"' };
export const step8Routes = {
    // ============================================================ meetings
    'meetings.list': defineRoute({
        id: 'meetings.list',
        method: 'GET',
        path: `${API_BASE}/meetings`,
        summary: 'The Meetings tab (and a client’s Meetings tab with client_id): newest first, with the four stats',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: LIST_SCOPE },
        query: ListMeetingsQuery,
        success: { status: 200, description: 'Meetings', schema: MeetingPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'meetings.get': defineRoute({
        id: 'meetings.get',
        method: 'GET',
        path: `${API_BASE}/meetings/{id}`,
        summary: 'One meeting: minutes, decisions, the staged tasks, media and transcript state (the drawer)',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: SCOPE },
        params: IdOnly,
        success: { status: 200, description: 'The meeting', schema: Meeting, headers: ETAG },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'meetings.create': defineRoute({
        id: 'meetings.create',
        method: 'POST',
        path: `${API_BASE}/meetings`,
        summary: '"Log a meeting": typed minutes, a transcript made in the browser and edited, or a link to a recording — always a draft',
        description: 'Typed minutes with tasks or decisions: the draft holds exactly those. Otherwise the worker proposes the ' +
            'summary, decisions and tasks (extraction_state). media_kind recorded WITH transcript_source live (a live recording of up to 2 minutes) ' +
            '= a recording transcribed IN THE BROWSER whose edited text is minutes_text; no audio is ever sent, and the ' +
            'draft is transcript_state ready. It needs consent_confirmed while recordings.require_consent is on (422 on ' +
            'consent_confirmed). media_kind recorded WITHOUT transcript_source = link only: recording_url required, no ' +
            'minutes_text, nothing read or extracted (the person writes the summary and tasks). held_on in the future is ' +
            '422. media_kind upload WITH transcript_source document = minutes from a document: the text POST ' +
            '/meetings/document/read returned, as edited, plus document_filename; extraction queued, no consent, nothing stored.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        headers: IdempotencyHeaders,
        body: CreateMeetingBody,
        success: { status: 201, description: 'The draft meeting', schema: Meeting, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'meetings.readDocument': defineRoute({
        id: 'meetings.readDocument',
        method: 'POST',
        path: `${API_BASE}/meetings/document/read`,
        summary: 'Read a minutes document (PDF, Word, Excel, CSV, text, Markdown) to text, to edit before "Log a meeting"',
        description: 'multipart/form-data: ONE file field `file` (.pdf/.docx/.xlsx/.csv/.txt/.md, at most 10 MB). Returns the text ' +
            '(line breaks kept), its length and the file name. Read-only: no meeting is made and nothing is stored; Submit ' +
            'is POST /meetings with media_kind upload, transcript_source document. A scanned or empty PDF, an empty or ' +
            'unopenable file, another file type, a file over 10 MB, or text over 100,000 characters is 422 ' +
            'validation_failed with a field error on file saying which (nothing is ever cut).',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: 'Any client-work role; no client is named and nothing is written.' },
        body: ReadMinutesDocumentBody,
        bodyEncoding: 'multipart',
        fileFields: ['file'],
        maxFileBytes: MINUTES_FILE_MAX_BYTES,
        success: { status: 200, description: 'The text of the document', schema: ReadMinutesDocumentResponse },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    'meetings.update': defineRoute({
        id: 'meetings.update',
        method: 'PATCH',
        path: `${API_BASE}/meetings/{id}`,
        summary: 'Edit the draft before confirming: title, date, who was there, summary, decisions, staged tasks',
        description: 'Lists sent replace the current ones. A confirmed meeting is 409 ("Confirmed minutes cannot be changed").',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        params: IdOnly,
        headers: IfMatchHeaders,
        body: UpdateMeetingBody,
        success: { status: 200, description: 'The draft', schema: Meeting, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed', 'conflict'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'meetings.confirm': defineRoute({
        id: 'meetings.confirm',
        method: 'POST',
        path: `${API_BASE}/meetings/{id}/confirm`,
        summary: '"Confirm minutes": create the ticked tasks, log the unticked ones with their reason — once',
        description: 'One transaction. Ticked tasks are created for the client’s SPOC (or the named person — needs task.reassign, ' +
            'else 403), source "Meeting minutes", linked back to the meeting. Unticked ones land in Dropped tasks. Every ' +
            'staged task must be listed (422). Domain-idempotent: confirming a confirmed meeting returns the first result ' +
            'and creates nothing. 409 while an extraction (or an older meeting’s transcript) is still queued or running.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        params: IdOnly,
        headers: OptionalIfMatchHeaders,
        body: ConfirmMeetingBody,
        success: { status: 200, description: 'The confirmed meeting and what it made', schema: ConfirmMeetingResponse },
        preconditionCurrent: Meeting,
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed', 'conflict'],
        idempotency: 'safe',
        concurrency: 'if-match-optional',
        rateLimit: 'write',
    }),
    'meetings.mediaUrl': defineRoute({
        id: 'meetings.mediaUrl',
        method: 'GET',
        path: `${API_BASE}/meetings/{id}/media-url`,
        summary: 'A short-lived link to open the minutes document (or an older meeting’s stored recording)',
        description: 'Opens only for the person who added it and managers (403 for the SPOC otherwise). 404 when nothing is ' +
            'stored (typed minutes, a browser transcript, a link) or old audio was deleted under retention. Every open is audited. The link is ' +
            'same-origin (`/api/v1/files/{token}`, see `files.get`), streams with Range support, and works only for the signed-in person it was issued to.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: 'The person who added the media, or a manager. Others who can see the meeting: 403; the rest 404.' },
        params: IdOnly,
        success: { status: 200, description: 'Same-origin file link (expires in 10 minutes)', schema: MediaUrlResponse },
        errors: ['no_session', 'forbidden', 'not_found', 'upstream_unavailable'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ email threads
    'emailThreads.list': defineRoute({
        id: 'emailThreads.list',
        method: 'GET',
        path: `${API_BASE}/email-threads`,
        summary: 'The Emails tab (and a client’s Emails panel): threads, newest activity first, with stats and coverage',
        description: 'tone: ToneForManagers for the founder and associate director; ToneLabelOnly for a member only while ' +
            'tone.show_label_to_members is on; absent otherwise, and for everyone while tone detection is off. Unmatched ' +
            'threads show to managers and to the SPOC of any client they could belong to.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: LIST_SCOPE },
        query: ListEmailThreadsQuery,
        success: {
            status: 200,
            description: 'Threads',
            schema: EmailThreadPage,
            byAudience: { revenue: EmailThreadPageManager, other: EmailThreadPageMember },
        },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'emailThreads.get': defineRoute({
        id: 'emailThreads.get',
        method: 'GET',
        path: `${API_BASE}/email-threads/{id}`,
        summary: 'One thread (the Email drawer) — subject, snippet, state, tone as the caller may see it',
        description: 'No message bodies exist to return. The drawer lists the thread’s proposals with GET /proposed-items?thread_id=.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: SCOPE },
        params: IdOnly,
        success: {
            status: 200,
            description: 'The thread',
            schema: EmailThread,
            byAudience: { revenue: EmailThreadManager, other: EmailThreadMember },
            headers: ETAG,
        },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'emailThreads.update': defineRoute({
        id: 'emailThreads.update',
        method: 'PATCH',
        path: `${API_BASE}/email-threads/{id}`,
        summary: 'Assign a thread to a client, correct its state, or mark it "Not a task" (with a reason)',
        description: 'Assigning an unmatched thread: client_id must be one of its candidate_clients (422). Who may assign: a manager ' +
            '(any candidate; any client for a matched thread); an ops member only into a client they are the SPOC of — any ' +
            'other client is 403. The response offers the participant domains to add to that client (domain_offer). Marking ' +
            '"Not a task" logs a Dropped tasks row and rejects the thread’s pending proposals with the same reason.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: THREAD_WRITE_SCOPE },
        params: IdOnly,
        headers: IfMatchHeaders,
        body: UpdateEmailThreadBody,
        success: {
            status: 200,
            description: 'The thread after the change',
            schema: UpdateEmailThreadResponse,
            byAudience: { revenue: UpdateEmailThreadResponseManager, other: UpdateEmailThreadResponseMember },
        },
        preconditionCurrent: EmailThread,
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed', 'conflict'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'emailThreads.makeTask': defineRoute({
        id: 'emailThreads.makeTask',
        method: 'POST',
        path: `${API_BASE}/email-threads/{id}/task`,
        summary: '"Make task" on a thread: a task (or an escalation) for the client’s SPOC, linked to the thread',
        description: '409 while the thread is unmatched or once it is linked. Someone other than the SPOC needs task.reassign (403).',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        params: IdOnly,
        headers: IdempotencyHeaders,
        body: MakeThreadTaskBody,
        success: {
            status: 201,
            description: 'What was made, and the thread',
            schema: MadeFromEmail,
            byAudience: { revenue: MadeFromEmailManager, other: MadeFromEmailMember },
        },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    // ============================================================ proposed items (from email)
    'proposedItems.list': defineRoute({
        id: 'proposedItems.list',
        method: 'GET',
        path: `${API_BASE}/proposed-items`,
        summary: 'Tasks and escalations proposed from client email, waiting for the SPOC (managers: all; late = 4 h+)',
        access: {
            auth: 'session',
            anyOf: CLIENT_WORK,
            note: 'Managers → all; an ops member → those for their clients or suggested to them. Others: 403.',
        },
        query: ListProposedItemsQuery,
        success: { status: 200, description: 'Proposals, oldest pending first', schema: ProposedItemPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'proposedItems.confirm': defineRoute({
        id: 'proposedItems.confirm',
        method: 'POST',
        path: `${API_BASE}/proposed-items/{id}/confirm`,
        summary: '"Confirm": the proposal becomes a task (source Email) or an escalation, linked to its thread',
        description: 'Never urgent by itself (ADR-003 §4.4): due today → high, otherwise medium. Another assignee than the SPOC needs ' +
            'task.reassign (403). 409 once decided.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: DECIDE_SCOPE },
        params: IdOnly,
        headers: IdempotencyHeaders,
        body: ConfirmProposedBody,
        success: { status: 201, description: 'The decided proposal and what it made', schema: ConfirmProposedResponse },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'proposedItems.reject': defineRoute({
        id: 'proposedItems.reject',
        method: 'POST',
        path: `${API_BASE}/proposed-items/{id}/reject`,
        summary: '"Reject": with a reason; logged in Dropped tasks as "Email"',
        description: 'Domain-idempotent, like confirming a meeting: rejecting a proposal that is already rejected writes nothing and ' +
            'returns the first result (its dropped item, with the first reason), so a retry after a lost response is a 200. ' +
            '409 once confirmed.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: DECIDE_SCOPE },
        params: IdOnly,
        body: RejectProposedBody,
        success: { status: 200, description: 'The rejected proposal and its dropped item', schema: RejectProposedResponse },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ documents (and the Approvals board)
    'documents.list': defineRoute({
        id: 'documents.list',
        method: 'GET',
        path: `${API_BASE}/documents`,
        summary: 'The Documents tab and the Approvals board: by stage, type, client or owner, with the board’s stats',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: LIST_SCOPE },
        query: ListDocumentsQuery,
        success: { status: 200, description: 'Documents, most recently changed first', schema: DocumentPage },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'documents.get': defineRoute({
        id: 'documents.get',
        method: 'GET',
        path: `${API_BASE}/documents/{id}`,
        summary: 'One document with its version history (the drawer)',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: SCOPE },
        params: IdOnly,
        success: { status: 200, description: 'The document', schema: Document, headers: ETAG },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'documents.signUpload': defineRoute({
        id: 'documents.signUpload',
        method: 'POST',
        path: `${API_BASE}/documents/sign-upload`,
        summary: 'Get an upload slot for a document file',
        description: 'The browser PUTs the file straight to storage, then sends the key as file_key. Bound to the caller; expires unused after 24 hours.' +
            ' 429 rate_limited (Postgres file store only) when the caller’s, or everyone’s, uploads not yet attached to anything are at their cap (FILES_PENDING_USER_MB / FILES_PENDING_TOTAL_MB), or the caller’s link uploads of the last 24 hours, attached or not, are at their day cap (FILES_UPLOAD_USER_DAY_MB); uploads nothing uses are deleted after 24 hours.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: 'Managers and ops members. Others: 403. Not while viewing as someone (403).' },
        headers: IdempotencyHeaders,
        body: SignDocumentUploadBody,
        success: { status: 201, description: 'Upload slot', schema: UploadSlot },
        errors: ['no_session', 'forbidden', 'validation_failed', 'conflict', 'rate_limited', 'upstream_unavailable'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'documents.create': defineRoute({
        id: 'documents.create',
        method: 'POST',
        path: `${API_BASE}/documents`,
        summary: '"Upload a file" or "New item for approval" — starts in Drafting',
        description: 'A file_key that is not the caller’s or not uploaded is 422. owner_id other than the caller needs a manager (403).',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        headers: IdempotencyHeaders,
        body: CreateDocumentBody,
        success: { status: 201, description: 'The document', schema: Document, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'documents.update': defineRoute({
        id: 'documents.update',
        method: 'PATCH',
        path: `${API_BASE}/documents/{id}`,
        summary: 'Rename, retype, change the owner, or update the "Latest email" line',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        params: IdOnly,
        headers: IfMatchHeaders,
        body: UpdateDocumentBody,
        success: { status: 200, description: 'The document', schema: Document, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'documents.addVersion': defineRoute({
        id: 'documents.addVersion',
        method: 'POST',
        path: `${API_BASE}/documents/{id}/versions`,
        summary: 'Upload a new file for a document (fills the open revision, or starts the next one)',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        params: IdOnly,
        headers: IdempotencyHeaders,
        body: AddDocumentVersionBody,
        success: { status: 201, description: 'The document with the new version', schema: Document, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'documents.move': defineRoute({
        id: 'documents.move',
        method: 'POST',
        path: `${API_BASE}/documents/{id}/stage`,
        summary: '"Move to" a stage; Changes requested opens the next revision; the days-in-stage clock restarts',
        description: 'Any move is allowed. Moving to the stage it is already in is 409.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        params: IdOnly,
        headers: OptionalIfMatchHeaders,
        body: MoveDocumentBody,
        success: { status: 200, description: 'The document', schema: Document, headers: ETAG },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed', 'conflict'],
        idempotency: 'none',
        concurrency: 'if-match-optional',
        rateLimit: 'write',
    }),
    'documents.fileUrl': defineRoute({
        id: 'documents.fileUrl',
        method: 'GET',
        path: `${API_BASE}/documents/{id}/versions/{vid}/file`,
        summary: '"Open": a short-lived link to one version’s file',
        description: 'The link is same-origin (`/api/v1/files/{token}`, see `files.get`) and works only for the signed-in person it was issued to. 404 for a revision without a file.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: SCOPE },
        params: DocumentVersionParams,
        success: { status: 200, description: 'Same-origin file link (expires in 10 minutes)', schema: MediaUrlResponse },
        errors: ['no_session', 'not_found', 'upstream_unavailable'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    // ============================================================ opportunities
    'opportunities.list': defineRoute({
        id: 'opportunities.list',
        method: 'GET',
        path: `${API_BASE}/opportunities`,
        summary: 'The Opportunities board: by stage, with counts (value only for the founder and associate director)',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: LIST_SCOPE },
        query: ListOpportunitiesQuery,
        success: {
            status: 200,
            description: 'Opportunities, newest first',
            schema: OpportunityPage,
            byAudience: { revenue: OpportunityPageManager, other: OpportunityPageMember },
        },
        errors: ['no_session', 'forbidden', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'opportunities.get': defineRoute({
        id: 'opportunities.get',
        method: 'GET',
        path: `${API_BASE}/opportunities/{id}`,
        summary: 'One opportunity (the drawer)',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: SCOPE },
        params: IdOnly,
        success: {
            status: 200,
            description: 'The opportunity',
            schema: Opportunity,
            byAudience: { revenue: OpportunityManager, other: OpportunityMember },
            headers: ETAG,
        },
        errors: ['no_session', 'not_found'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'opportunities.create': defineRoute({
        id: 'opportunities.create',
        method: 'POST',
        path: `${API_BASE}/opportunities`,
        summary: '"Add an opportunity" — starts in Spotted',
        description: 'value_lakh / value_unknown need revenue.read (403).',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        headers: IdempotencyHeaders,
        body: CreateOpportunityBody,
        success: {
            status: 201,
            description: 'The opportunity',
            schema: Opportunity,
            byAudience: { revenue: OpportunityManager, other: OpportunityMember },
            headers: ETAG,
        },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    'opportunities.update': defineRoute({
        id: 'opportunities.update',
        method: 'PATCH',
        path: `${API_BASE}/opportunities/{id}`,
        summary: 'Edit an opportunity — the value fields only for the founder and associate director',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        params: IdOnly,
        headers: IfMatchHeaders,
        body: UpdateOpportunityBody,
        success: {
            status: 200,
            description: 'The opportunity',
            schema: Opportunity,
            byAudience: { revenue: OpportunityManager, other: OpportunityMember },
            headers: ETAG,
        },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
    'opportunities.move': defineRoute({
        id: 'opportunities.move',
        method: 'POST',
        path: `${API_BASE}/opportunities/{id}/stage`,
        summary: '"Move to" a stage; handing to sales may name the sales person',
        description: 'handed_to_id must be an active sales person (422). Moving to the stage it is already in is 409.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: WRITE_SCOPE },
        params: IdOnly,
        headers: OptionalIfMatchHeaders,
        body: MoveOpportunityBody,
        success: {
            status: 200,
            description: 'The opportunity',
            schema: Opportunity,
            byAudience: { revenue: OpportunityManager, other: OpportunityMember },
            headers: ETAG,
        },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'precondition_failed', 'conflict'],
        idempotency: 'none',
        concurrency: 'if-match-optional',
        rateLimit: 'write',
    }),
    // ============================================================ WhatsApp (no API — a wa.me link, "marked as sent")
    'whatsapp.draft': defineRoute({
        id: 'whatsapp.draft',
        method: 'GET',
        path: `${API_BASE}/whatsapp/draft`,
        summary: 'Compose a WhatsApp message from ticked points, in a tone — to a client, or to a team member',
        description: 'To a client: a manager or the client’s SPOC (404 otherwise). To a team member: managers only (403). Writes nothing.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: 'Client: managers or the client’s SPOC (404). Team member: managers (403).' },
        query: WhatsAppDraftQuery,
        success: { status: 200, description: 'The draft', schema: WhatsAppDraft },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'whatsapp.markSent': defineRoute({
        id: 'whatsapp.markSent',
        method: 'POST',
        path: `${API_BASE}/whatsapp/sent`,
        summary: 'Record "Marked as sent by you" — never "delivered"',
        description: 'Keeps a hash of the text, not the text. With a client activity: logs it done today (via whatsapp_link). With a ' +
            'team member: records the nudge. Same access as the draft.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: 'Client: managers or the client’s SPOC (404). Team member: managers (403). Not while viewing as someone (403).' },
        headers: IdempotencyHeaders,
        body: MarkWhatsAppSentBody,
        success: { status: 201, description: 'What was recorded', schema: MarkWhatsAppSentResponse },
        errors: ['no_session', 'forbidden', 'not_found', 'validation_failed', 'conflict'],
        idempotency: 'key',
        rateLimit: 'write',
    }),
    // ============================================================ Google connection (ADR-003 §9)
    'google.me': defineRoute({
        id: 'google.me',
        method: 'GET',
        path: `${API_BASE}/me/google`,
        summary: 'My Gmail and Calendar connection: state, granted scopes, last success — never token material',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: `${SELF} Others: 403.` },
        success: { status: 200, description: 'Connection state', schema: MyGoogleConnection },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'google.connect': defineRoute({
        id: 'google.connect',
        method: 'GET',
        path: `${API_BASE}/auth/google/connect`,
        summary: 'Start connecting Gmail or Calendar (read-only), after the in-app disclosure',
        description: 'Redirects to Google asking for gmail.readonly or calendar.readonly with access_type=offline, ' +
            'include_granted_scopes=true, prompt=consent, login_hint and a single-use state (10 minutes). Not while viewing ' +
            'or acting as someone (403).',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: `${SELF} Others: 403.` },
        query: GoogleConnectQuery,
        success: { status: 302, description: 'Redirect to Google', headers: { Location: 'accounts.google.com URL' } },
        errors: ['no_session', 'forbidden', 'validation_failed', 'upstream_unavailable'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'google.connectCallback': defineRoute({
        id: 'google.connectCallback',
        method: 'GET',
        path: `${API_BASE}/auth/google/connect/callback`,
        summary: 'Finish connecting: verify the account and the granted scope, store the encrypted grant, start the backfill',
        description: 'The Google account must be the signed-in person’s. Only what Google actually granted is stored. Success and ' +
            'failure both redirect to the redirect path; failures add ?google_error=<GoogleConnectError>. The connection is ' +
            'audited as an explicit event (the token table itself is never audited).',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: 'The person who started the connection (Google redirects here).' },
        query: GoogleConnectCallbackQuery,
        success: { status: 302, description: 'Back into the app', headers: { Location: 'redirect path, with ?google_error=<code> on failure' } },
        errors: ['no_session', 'forbidden', 'validation_failed', 'upstream_unavailable'],
        idempotency: 'none',
        rateLimit: 'write',
    }),
    'google.disconnect': defineRoute({
        id: 'google.disconnect',
        method: 'DELETE',
        path: `${API_BASE}/me/google/{scope}`,
        summary: 'Disconnect: revoke at Google (which removes BOTH scopes), delete the grant and my mailbox’s email rows',
        description: 'Email-derived rows only this person’s mailbox surfaced are deleted (ADR-003 §5.4); threads another connected ' +
            'member also sees stay. For an ops role the email and calendar surfaces prompt to connect again.',
        access: { auth: 'session', anyOf: CLIENT_WORK, deny: 'forbidden', note: `${SELF} Not while viewing or acting as someone (403).` },
        params: GoogleScopeParams,
        success: { status: 200, description: 'Disconnected', schema: DisconnectGoogleResponse },
        errors: ['no_session', 'forbidden', 'upstream_unavailable'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    'google.coverage': defineRoute({
        id: 'google.coverage',
        method: 'GET',
        path: `${API_BASE}/integrations/google`,
        summary: 'Who has connected email and calendar, and which clients have no email signals because of it',
        access: { auth: 'session', anyOf: MANAGERS, note: 'Founder and associate director. Others: 403.' },
        success: { status: 200, description: 'Coverage, people with a missing required connection first', schema: GoogleCoverage },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'google.resync': defineRoute({
        id: 'google.resync',
        method: 'POST',
        path: `${API_BASE}/integrations/google/{user_id}/resync`,
        summary: 'Poll a member’s mailbox and calendar now (cannot create a grant)',
        description: '409 when the member has no working grant.',
        access: { auth: 'session', anyOf: MANAGERS, deny: 'forbidden', note: 'Founder and associate director. Others: 403.' },
        params: GoogleUserParams,
        success: { status: 202, description: 'Queued', schema: ResyncResponse },
        errors: ['no_session', 'forbidden', 'not_found', 'conflict'],
        idempotency: 'safe',
        rateLimit: 'write',
    }),
    // ============================================================ settings: recordings and privacy
    'settings.privacy': defineRoute({
        id: 'settings.privacy',
        method: 'GET',
        path: `${API_BASE}/settings/privacy`,
        summary: 'Recordings and privacy: consent before audio upload, audio retention, the consent text, tone label for members',
        description: 'Readable by everyone who logs meetings, so the "Log a meeting" form knows whether to ask for consent.',
        access: { auth: 'session', anyOf: CLIENT_WORK, note: 'Managers and ops members read; only managers change it. Others: 403.' },
        success: { status: 200, description: 'The settings', schema: PrivacySettings, headers: CONFIG_ETAG },
        errors: ['no_session', 'forbidden'],
        idempotency: 'safe',
        rateLimit: 'default',
    }),
    'settings.updatePrivacy': defineRoute({
        id: 'settings.updatePrivacy',
        method: 'PATCH',
        path: `${API_BASE}/settings/privacy`,
        summary: 'Change the recordings and privacy settings',
        description: 'If-Match is the config_version (shared with the other Settings groups). require_consent and ' +
            'show_label_to_members need settings.write (founder and associate director). recordings.retention_days ' +
            'additionally needs settings.system — the founder; anyone else sending it gets 403 with field_errors on ' +
            'recordings.retention_days and nothing changes. Bumps config_version; audited.',
        access: { auth: 'session', anyOf: MANAGER_SETTINGS, note: 'Founder and associate director; audio retention (recordings.retention_days): the founder (system admin), checked by the handler. Others: 403.' },
        headers: IfMatchHeaders,
        body: PatchPrivacySettingsBody,
        success: { status: 200, description: 'The settings after the change', schema: PrivacySettings, headers: CONFIG_ETAG },
        errors: ['no_session', 'forbidden', 'validation_failed', 'precondition_failed'],
        idempotency: 'safe',
        concurrency: 'if-match',
        rateLimit: 'write',
    }),
};
//# sourceMappingURL=routes.step8.js.map